/**
 * Road to Submission Data Fetcher
 * Fetches and computes college submission readiness data
 */

import type {
  SubmissionCollege,
  SupplementProgress,
  CollegeDeadline,
  GlobalStats,
  RoadToSubmissionResult,
} from "./types";
import {
  computeSubmissionStatus,
  calculateExpectedPercent,
  createShortLabel,
} from "./utils";
import {
  getFavorites,
  getCollegesByIds,
  getCurrentUser,
  getApplicationHearts,
} from "../../services/college";
import { getCollegeDeadlines } from "../../services/calendarApi";

/**
 * Map early_type to short labels
 */
const EARLY_TYPE_TO_LABEL: Record<string, string> = {
  "Early Decision I (ED I)": "ED I",
  "Early Decision II (ED II)": "ED II",
  "Early Decision (ED)": "ED",
  "Early Action (EA)": "EA",
  "Early Action(EA)": "EA",
  "Single-Choice Early Action (SCEA)": "SCEA",
  "Restrictive Early Action (REA)": "REA",
  "ED I / EA": "ED/EA",
  "Early Action (also ED I/II)": "ED/EA",
  "Early Action 1 (GA residents)": "EA I",
  "Early Action 2 (non-GA)": "EA II",
};

/**
 * Fetch Road to Submission data from favorites and deadlines
 * @param localFavorites - Favorites from Zustand store (localStorage) as fallback
 * @param detailedSupplements - Supplement progress data
 * @param globalStats - Global module stats for readiness calculation
 */
export async function fetchRoadToSubmissionData(
  localFavorites: string[] = [],
  detailedSupplements: SupplementProgress[] = [],
  globalStats: GlobalStats,
): Promise<RoadToSubmissionResult> {
  try {
    // Get current user
    const user = await getCurrentUser();
    if (!user?.id) {
      return { selectedCollegeId: "", colleges: [] };
    }

    // Get favorite college IDs from database first
    let favoriteIds: string[] = [];
    try {
      favoriteIds = await getFavorites(user.id);
    } catch (dbError) {
    }

    // If DB has no favorites, use localStorage favorites as fallback
    if (favoriteIds.length === 0 && localFavorites.length > 0) {
      favoriteIds = localFavorites;
    }

    // Get application hearts (for prioritization)
    let heartedIds: Set<string> = new Set();
    try {
      const hearts = await getApplicationHearts(user.id);
      // Create composite keys: "123-rd", "456-ea"
      hearts.forEach((h) => {
        heartedIds.add(`${h.collegeId}-${h.applicationType.toLowerCase()}`);
      });
    } catch (e) {
    }

    if (favoriteIds.length === 0) {      return { selectedCollegeId: "", colleges: [] };
    }

    // Get college details for favorites
    const colleges = await getCollegesByIds(favoriteIds);
    if (colleges.length === 0) {
      return { selectedCollegeId: "", colleges: [] };
    }

    // Get deadlines for these colleges (using college_id for efficiency)
    const collegeIds = colleges
      .map((c) => parseInt(c.id, 10))
      .filter((id) => !isNaN(id));
    const deadlines = await getCollegeDeadlines(collegeIds);

    // Create a map of college_id to ALL deadline rows (colleges can have multiple rows)
    const deadlineMap = new Map<number, CollegeDeadline[]>();
    deadlines.forEach((d) => {
      if (d.college_id) {
        if (!deadlineMap.has(d.college_id)) {
          deadlineMap.set(d.college_id, []);
        }
        deadlineMap.get(d.college_id)!.push(d);
      }
    });

    // Helper to find all deadline rows for a college by ID
    const findDeadlines = (collegeId: string): CollegeDeadline[] => {
      const numId = parseInt(collegeId, 10);
      if (isNaN(numId)) return [];
      return deadlineMap.get(numId) || [];
    };

    // Helper to find detailed progress for a college with robust matching
    const findSupplementProgress = (collegeId: string) => {
      if (!detailedSupplements || detailedSupplements.length === 0)
        return undefined;

      // 1. Exact string match
      const exact = detailedSupplements.find(
        (p) => String(p.collegeId) === String(collegeId),
      );
      if (exact) return exact;

      // 2. Trimmed string match
      const trimmed = detailedSupplements.find(
        (p) => String(p.collegeId).trim() === String(collegeId).trim(),
      );
      if (trimmed) return trimmed;

      // 3. Numeric match
      const numId = parseInt(collegeId);
      if (!isNaN(numId)) {
        return detailedSupplements.find(
          (p) => parseInt(String(p.collegeId)) === numId,
        );
      }
      return undefined;
    };

    // Build submission colleges array
    const submissionColleges: SubmissionCollege[] = [];
    // Track which RD deadlines we've added to avoid duplicates
    const addedRdDeadlines = new Set<string>();

    for (const college of colleges) {
      const deadlineRows = findDeadlines(college.id);

      // Calculate college-specific readiness
      // Formula: (Colleges + Essays + Specific Supplements + Activities + RateMyChance + Research) / 6
      const suppProgress = findSupplementProgress(college.id);
      let suppPercent = 0;

      if (suppProgress) {
        if (suppProgress.total > 0) {
          suppPercent = Math.min(
            100,
            Math.round((suppProgress.completed / suppProgress.total) * 100),
          );
        }
      }

      const totalScore =
        globalStats.collegesPercent +
        globalStats.essaysPercent +
        suppPercent +
        globalStats.activitiesPercent +
        globalStats.rateMyChancePercent +
        globalStats.researchPercent;

      const readiness = Math.round(totalScore / 6);

      if (deadlineRows.length > 0) {
        // Process each deadline row (college may have multiple: ED I, ED II, etc.)
        for (const deadline of deadlineRows) {
          // Add early deadline if valid early_type
          if (deadline.early_deadline && deadline.early_type) {
            const shortLabel = EARLY_TYPE_TO_LABEL[deadline.early_type];
            if (shortLabel) {
              // Map short label to decision type for ID
              let decisionType: "EA" | "ED" | "ED2" | "RD" | "Rolling" = "ED";
              if (shortLabel.includes("EA")) decisionType = "EA";
              else if (shortLabel === "ED II") decisionType = "ED2";

              const earlyExpected = calculateExpectedPercent(
                deadline.early_deadline,
              );
              const idSuffix = shortLabel
                .toLowerCase()
                .replace(/[^a-z0-9]/g, "");

              submissionColleges.push({
                id: `${college.id}-${idSuffix}`,
                name: college.name,
                shortLabel: createShortLabel(college.name, shortLabel),
                decisionType: decisionType,
                deadline: deadline.early_deadline,
                readinessPercent: readiness,
                expectedPercentToday: earlyExpected,
                isPrimary: false,
                isStarred: heartedIds.has(`${college.id}-${idSuffix}`),
                status: computeSubmissionStatus(
                  deadline.early_deadline,
                  readiness,
                  earlyExpected,
                ),
              });
            }
          }

          // Add RD deadline (deduplicate - only add once per college)
          if (deadline.regular_deadline) {
            const rdKey = `${college.id}-rd`;
            if (!addedRdDeadlines.has(rdKey)) {
              addedRdDeadlines.add(rdKey);

              const rdExpected = calculateExpectedPercent(
                deadline.regular_deadline,
              );
              submissionColleges.push({
                id: rdKey,
                name: college.name,
                shortLabel: createShortLabel(college.name, "RD"),
                decisionType: "RD",
                deadline: deadline.regular_deadline,
                readinessPercent: readiness,
                expectedPercentToday: rdExpected,
                isPrimary: false,
                isStarred: heartedIds.has(rdKey),
                status: computeSubmissionStatus(
                  deadline.regular_deadline,
                  readiness,
                  rdExpected,
                ),
              });
            }
          }
        }
      } else {
        // No deadline found, use a default (Jan 1 of next year for RD)
        const nextYear = new Date().getFullYear() + 1;
        const defaultDeadline = `${nextYear}-01-01`;
        const expectedPercent = calculateExpectedPercent(defaultDeadline);

        submissionColleges.push({
          id: `${college.id}-rd`,
          name: college.name,
          shortLabel: createShortLabel(college.name, "RD"),
          decisionType: "RD",
          deadline: defaultDeadline,
          readinessPercent: readiness,
          expectedPercentToday: expectedPercent,
          isPrimary: false,
          isStarred: heartedIds.has(`${college.id}-rd`),
          status: computeSubmissionStatus(
            defaultDeadline,
            readiness,
            expectedPercent,
          ),
        });
      }
    }

    // Sort by deadline (earliest first)
    submissionColleges.sort(
      (a, b) => new Date(a.deadline).getTime() - new Date(b.deadline).getTime(),
    );

    // Set the first one as primary
    if (submissionColleges.length > 0) {
      submissionColleges[0].isPrimary = true;
    }    return {
      selectedCollegeId: submissionColleges[0]?.id || "",
      colleges: submissionColleges,
    };
  } catch (error) {
    console.error("❌ [RoadToSubmission] Error fetching data:", error);
    return { selectedCollegeId: "", colleges: [] };
  }
}
